/* eslint-disable max-lines */
import _ from 'lodash';
import styled from 'styled-components';
import { Tooltip } from '@material-ui/core';
import { pluralize } from 'common/utils/utils';
import { PROVIDERS_MAP } from 'common/constants/constants';
import { SPACES } from 'common/constants/sizes';
import Checkbox from 'components/base/Checkbox';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import ChipEntityType from './ChipEntityType';
import { META_DATA_STATUSES } from './hooks/PMHooks/useProviderMetaData';
import IssuesNumberCell from './components/table-cells/IssuesNumberCell';
import DateCell from './components/table-cells/DateCell';

const UsedByTexts = styled(Flex)`
  p:not(:last-child) {
    padding-bottom: 0.4rem;
  }
`;
const AvatarUrl = styled.img`
  height: 100%;
  width: 2rem;
  padding-left: 1rem;
`;

const getNameCol = (entityName, setAllSelected = null, allSelected = false) => {
  const selectAllEnabled = !!setAllSelected;
  return {
    name: `${entityName.toUpperCase()} NAME`,
    dataPoint: 'name',
    ColumnHeaderComponent: () => (
      <Flex items='center'>
        {selectAllEnabled && (
          <Checkbox
            checked={allSelected}
            indeterminate={allSelected}
            onChange={e => setAllSelected(e.target.checked)}
          />
        )}
        <Text color='gray' letterSpacing='0.01071em' pl='1rem'>
          {entityName.toUpperCase()} NAME
        </Text>
      </Flex>
    )
  };
};

export const getTableColumnsByProviderName = (
  providerName,
  ignorePmMetaDataEnabled,
  entityName,
  onSelectAllClicked = null,
  allSelected = false
) =>
  TABLE_COLS_BY_PROVIDER_NAME[providerName](entityName)(
    ignorePmMetaDataEnabled,
    onSelectAllClicked,
    allSelected
  );

const CLUBHOUSE_TABLE_COLS =
  (entityName = PROVIDERS_MAP.clubhouse.entity_name) =>
  (ignorePmMetaDataEnabled = false) => {
    return [
      getNameCol(entityName),
      {
        name: 'DESCRIPTION',
        dataPoint: 'description'
      },
      {
        name: 'USED BY',
        dataPoint: 'usedBy'
      },
      {
        name: 'STORIES',
        dataPoint: 'num_stories_cell',
        isSortedCol: false,
        sortPoint: 'num_stories'
      }
    ];
  };

const JIRA_TABLE_COLS =
  (entityName = PROVIDERS_MAP.jira.entity_name) =>
  (ignorePmMetaDataEnabled = false, setAllSelected, allSelected) => {
    const isProject = entityName === PROVIDERS_MAP.jira.entity_names.project;
    const projectColumns = [
      {
        name: 'PROJECT KEY',
        dataPoint: 'provider_key'
      },
      {
        name: 'PROJECT TYPE',
        dataPoint: 'project_type'
      }
    ];
    return [
      getNameCol(entityName, setAllSelected, allSelected),
      ...(isProject
        ? projectColumns
        : [
            {
              name: 'USED BY',
              dataPoint: 'usedBy'
            }
          ]),
      ignorePmMetaDataEnabled
        ? null
        : {
            name: 'UPDATED',
            dataPoint: 'updated_at'
          },
      ignorePmMetaDataEnabled
        ? null
        : {
            name: 'ISSUES',
            dataPoint: 'num_issues_cell',
            sortPoint: 'num_issues',
            isSortedCol: false
          }
    ].filter(Boolean);
  };

const AZURE_BOARDS_TABLE_COLS =
  (entityName = PROVIDERS_MAP.azure_boards.entity_name) =>
  (ignorePmMetaDataEnabled = false, setAllSelected, allSelected) => {
    return [
      getNameCol(entityName, setAllSelected, allSelected),
      ignorePmMetaDataEnabled
        ? null
        : {
            name: 'LAST ACTIVITY',
            dataPoint: 'updated_at'
          },
      ignorePmMetaDataEnabled
        ? null
        : {
            name: 'WORK ITEMS',
            dataPoint: 'num_issues_cell',
            sortPoint: 'num_issues',
            isSortedCol: false
          }
    ].filter(Boolean);
  };

const TABLE_COLS_BY_PROVIDER_NAME = {
  [PROVIDERS_MAP.clubhouse.name]: entityName =>
    CLUBHOUSE_TABLE_COLS(entityName),
  [PROVIDERS_MAP.jira.name]: entityName => JIRA_TABLE_COLS(entityName),
  [PROVIDERS_MAP.azure_boards.name]: entityName =>
    AZURE_BOARDS_TABLE_COLS(entityName)
};

const getName = ({
  isSelected,
  isUsedBy,
  toggleSelection,
  name,
  entityType,
  avatarUrl
}) => (
  <Flex items='center'>
    <Checkbox
      checked={isSelected}
      disabled={isUsedBy}
      onChange={toggleSelection}
    />
    {avatarUrl && <AvatarUrl src={avatarUrl} />}
    <Text size='medium' pl={SPACES.small}>
      {name}
    </Text>
    <ChipEntityType entityType={entityType} />
  </Flex>
);

const getIsUsedBy = (
  usedByTeams,
  usedByProjects,
  isSelected,
  hasBoardsInUsed
) => {
  return (
    (usedByTeams.length > 0 || usedByProjects.length > 0 || hasBoardsInUsed) &&
    isSelected
  );
};

const stylesTableCell = isUsedBy => {
  return isUsedBy
    ? {
        grayedOut: isUsedBy
      }
    : {};
};

const usedByMessage = (usedByTeams, usedByProjects) => {
  const usedByNumOfTeams = pluralize(usedByTeams.length, 'team');
  const usedByNumOfProjects = pluralize(usedByProjects.length, 'project');

  if (usedByTeams.length > 0 && usedByProjects.length > 0) {
    return `${usedByNumOfTeams} and ${usedByNumOfProjects}`;
  }

  if (usedByTeams.length > 0) {
    return usedByNumOfTeams;
  }

  if (usedByNumOfProjects.length > 0) {
    return usedByNumOfProjects;
  }
};

const nameTableCell = (
  usedByTeams,
  usedByProjects,
  isUsedBy,
  entityName,
  isSelected,
  toggleSelection,
  provider_id,
  name,
  entityType,
  avatarUrl = null
) => {
  const tooltipMessage =
    entityName === 'project'
      ? `This ${entityName} cannot be unselected because it has active boards associated with it`
      : `This ${entityName} cannot be unselected because it is used by ${usedByMessage(
          usedByTeams,
          usedByProjects
        )}`;
  return (
    <Tooltip disableHoverListener={!isUsedBy} title={tooltipMessage}>
      {getName({
        isSelected,
        isUsedBy,
        toggleSelection,
        provider_id,
        name,
        entityType,
        avatarUrl
      })}
    </Tooltip>
  );
};

const usedByTableCell = (usedByTeams, usedByProjects) => {
  return (
    <UsedByTexts direction='column'>
      {usedByTeams.length > 0 && (
        <Text size='medium' color='shuttleGray'>
          <b>Teams: </b>
          {usedByTeams.join(', ')}
        </Text>
      )}
      {usedByProjects.length > 0 && (
        <Text size='medium' color='shuttleGray'>
          <b>Projects: </b>
          {usedByProjects.join(', ')}
        </Text>
      )}
    </UsedByTexts>
  );
};

const getJiraTableData = (entity, entityName) => {
  const {
    name,
    num_issues,
    updated_at,
    usedByTeams = [],
    usedByProjects = [],
    hasBoardsInUsed = false,
    provider_id,
    isSelected,
    toggleSelection,
    metaStatus,
    avatar_urls: avatarUrls = {},
    provider_key: providerKey,
    project_type: projectType
  } = entity;
  const isUsedBy = getIsUsedBy(
    usedByTeams,
    usedByProjects,
    isSelected,
    hasBoardsInUsed
  );
  const isMetaDataError = metaStatus === META_DATA_STATUSES.ERROR;
  const isProject = entityName === PROVIDERS_MAP.jira.entity_names.project;
  const avatarUrl = isProject && _.get(avatarUrls, '24x24');

  return {
    ...entity,
    styles: stylesTableCell(isUsedBy),
    name: nameTableCell(
      usedByTeams,
      usedByProjects,
      isUsedBy,
      entityName,
      isSelected,
      toggleSelection,
      provider_id,
      name,
      null,
      avatarUrl
    ),
    ...(isProject
      ? {
          provider_key: <Text size='medium'>{providerKey || ''}</Text>,
          project_type: (
            <Text size='medium'>
              {projectType?.toUpperCase().replace('_', ' ') || ''}
            </Text>
          )
        }
      : {}),
    ...(isProject
      ? {}
      : { usedBy: usedByTableCell(usedByTeams, usedByProjects) }),
    updated_at: <DateCell date={updated_at} isError={isMetaDataError} />,
    num_issues_cell: (
      <IssuesNumberCell number={num_issues} isError={isMetaDataError} />
    )
  };
};

const getCHTableData = (entity, entityName) => {
  const {
    name,
    description,
    num_stories,
    provider_id,
    isSelected,
    toggleSelection,
    usedByTeams = [],
    usedByProjects = [],
    entityType,
    hasBoardsInUsed = false
  } = entity;

  const isUsedBy = getIsUsedBy(
    usedByTeams,
    usedByProjects,
    isSelected,
    hasBoardsInUsed
  );
  return {
    ...entity,
    styles: stylesTableCell(isUsedBy),
    name: nameTableCell(
      usedByTeams,
      usedByProjects,
      isUsedBy,
      entityName,
      isSelected,
      toggleSelection,
      provider_id,
      name,
      entityType
    ),
    description: <Text size='medium'>{description}</Text>,
    usedBy: usedByTableCell(usedByTeams, usedByProjects),
    num_stories_cell: <Text size='medium'>{num_stories}</Text>
  };
};

const getAzureBoardsTableData = (entity, entityName) => {
  const {
    name,
    num_issues,
    updated_at,
    usedByTeams = [],
    usedByProjects = [],
    provider_id,
    isSelected,
    toggleSelection,
    metaStatus,
    avatar_urls: avatarUrls = {},
    provider_key: providerKey,
    hasBoardsInUsed = false
  } = entity;
  const isUsedBy = getIsUsedBy(
    usedByTeams,
    usedByProjects,
    isSelected,
    hasBoardsInUsed
  );

  const isMetaDataError = metaStatus === META_DATA_STATUSES.ERROR;
  const avatarUrl = _.get(avatarUrls, '34x34');

  return {
    ...entity,
    styles: stylesTableCell(isUsedBy),
    name: nameTableCell(
      usedByTeams,
      usedByProjects,
      isUsedBy,
      entityName,
      isSelected,
      toggleSelection,
      provider_id,
      name,
      null,
      avatarUrl
    ),
    provider_key: <Text size='medium'>{providerKey || ''}</Text>,
    updated_at: <DateCell date={updated_at} isError={isMetaDataError} />,
    num_issues_cell: (
      <IssuesNumberCell number={num_issues} isError={isMetaDataError} />
    )
  };
};

const TABLE_DATA_BY_PROVIDER_NAME = {
  [PROVIDERS_MAP.clubhouse.name]: getCHTableData,
  [PROVIDERS_MAP.jira.name]: getJiraTableData,
  [PROVIDERS_MAP.azure_boards.name]: getAzureBoardsTableData
};

export const getTableDataByProviderName = providerName =>
  TABLE_DATA_BY_PROVIDER_NAME[providerName];
